import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import {
  Ruler, Square, ArrowUpDown, ArrowLeftRight, MousePointer2,
  Trash2, ChevronLeft, ChevronRight, Tag,
  ZoomIn, ZoomOut, Maximize2, X, Undo2, CornerDownRight, Minus, ChevronDown,
  FlipHorizontal, RotateCw, RotateCcw, ScanSearch, Focus,
} from "lucide-react";
import { drawAnnotations, regionCorners, computeFootPads, resolvePadSizeId, LINE_COLORS, FOOT_COLORS } from "../lib/annotationRenderer.js";

const pxToMm = (px, dpi) => ((px / dpi) * 25.4).toFixed(1);
const dist = (x1, y1, x2, y2) => Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
const mmToRuSize = (mm) => (!mm || isNaN(Number(mm)) ? null : Math.round(Number(mm) / 10 * 1.5 + 1));

const CORNER_HIT = 10;


// ── Custom tool dropdown ──────────────────────────────────────────────────────
function ToolDropdown({ tools, value, onChange, placeholder = "Инструмент" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = tools.find(t => t.id === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-2.5 h-7 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 transition-all"
      >
        {current ? (
          <>
            <span className="flex-shrink-0 text-slate-500">{current.icon}</span>
            <span>{current.label}</span>
          </>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <ChevronDown className="h-3 w-3 text-slate-400 ml-0.5" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 min-w-[130px]">
          {tools.map(t => (
            <button key={t.id} onClick={() => { onChange(t.id); setOpen(false); }}
              className={cn(
                "flex items-center gap-2.5 w-full px-3 py-1.5 text-xs text-left transition-colors",
                value === t.id ? "text-blue-600 bg-blue-50 font-semibold" : "text-slate-700 hover:bg-slate-50"
              )}>
              <span className="flex-shrink-0">{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pad size dropdown ─────────────────────────────────────────────────────────
function PadSizeDropdown({ sizes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = sizes.find(s => s.id === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0 flex items-center gap-1">
      <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">Валик:</span>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-2 h-7 rounded-lg text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 transition-all"
      >
        <span>{current?.name || "—"}</span>
        <ChevronDown className="h-3 w-3 text-purple-400" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 min-w-[130px]">
          {sizes.map(s => (
            <button key={s.id} onClick={() => { onChange(s.id); setOpen(false); }}
              className={cn(
                "flex items-center justify-between gap-3 w-full px-3 py-1.5 text-xs text-left transition-colors",
                value === s.id ? "text-purple-700 bg-purple-50 font-semibold" : "text-slate-700 hover:bg-slate-50"
              )}>
              <span>{s.name}</span>
              <span className="text-[10px] text-slate-400">{s.length_mm}×{s.width_mm}мм</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Foot size widget — flanks the Левая/Правая selector, replaces the old "Параметры стоп" tab ──
function FootSizeWidget({ side, align, scanParams, onScanParamsChange, allAnnotations }) {
  const data = scanParams[side] || {};
  const ruSize = mmToRuSize(data.footSizeMm);

  const measuredLength = allAnnotations
    .filter(a => a.foot === side || (side === "left" && !a.foot))
    .find(a => a.type === "size_line")?.distanceMm;

  useEffect(() => {
    if (measuredLength && !data.footSizeMm) {
      onScanParamsChange({ ...scanParams, [side]: { ...data, footSizeMm: String(Math.round(measuredLength)) } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuredLength]);

  return (
    <div className={cn("flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-xl px-2.5 py-1.5", align === "right" && "flex-row-reverse")}>
      <input
        type="number"
        min="150"
        max="350"
        placeholder="мм"
        value={data.footSizeMm || ""}
        onChange={e => onScanParamsChange({ ...scanParams, [side]: { ...data, footSizeMm: e.target.value } })}
        className="w-11 bg-transparent text-white text-xs text-center outline-none placeholder:text-white/40 [appearance:textfield]"
        title={side === "left" ? "Размер левой стопы, мм" : "Размер правой стопы, мм"}
      />
      <span className="text-white/50 text-[10px]">мм</span>
      {ruSize && <span className="text-white text-[10px] font-bold">RU {ruSize}</span>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ImageViewer({
  pages, currentPage, onPageChange, dpi, annotations, onAnnotationsChange,
  activeFoot = "left", onActiveFootChange,
  scanView = "original", onScanViewChange,
  onUndo, canUndo = false,
  onFlipPage, onRotatePage, onAutoDetectFeet,
  padSizes = [],
  onPadSizeChange,
  scanParams = {}, onScanParamsChange,
  allAnnotations = [],
}) {
  const [tool, setTool] = useState("measure");
  const [drawing, setDrawing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [localResizeAnns, setLocalResizeAnns] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [padSizesPerFoot, setPadSizesPerFoot] = useState({ left: null, right: null });

  const effectiveAnnotations = localResizeAnns ?? annotations;
  const [zoom, setZoom] = useState(1.0);
  const [natW, setNatW] = useState(0);
  const [natH, setNatH] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [imgMax, setImgMax] = useState({ w: window.innerWidth - 400, h: window.innerHeight - 130 });

  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const areaRef   = useRef(null);
  const zoomFeetTargetRef = useRef(null);

  const page = pages[currentPage];

  useEffect(() => {
    if (padSizes?.length) {
      setPadSizesPerFoot(p => {
        const next = { left: p.left ?? padSizes[0].id, right: p.right ?? padSizes[0].id };
        if (next.left !== p.left || next.right !== p.right) onPadSizeChange?.(next);
        return next;
      });
    }
  }, [padSizes]);

  const currentPadSizeId = padSizesPerFoot[activeFoot] ?? null;

  const handlePadSizeChange = (id) => {
    const next = { ...padSizesPerFoot, [activeFoot]: id };
    setPadSizesPerFoot(next);
    onPadSizeChange?.(next);
  };

  // ── Derived display size ──────────────────────────────────────────
  const fitScale = natW && natH ? Math.min(imgMax.w / natW, imgMax.h / natH) : 1;
  const displayW = natW ? Math.round(natW * fitScale * zoom) : undefined;
  const displayH = natH ? Math.round(natH * fitScale * zoom) : undefined;

  const getScale = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return 1;
    return img.getBoundingClientRect().width / img.naturalWidth;
  }, []);

  const evToImg = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scale = getScale();
    return { x: (e.clientX - rect.left) / scale, y: (e.clientY - rect.top) / scale };
  }, [getScale]);

  const zoomIn    = () => setZoom(z => Math.min(5, parseFloat((z * 1.25).toFixed(3))));
  const zoomOut   = () => setZoom(z => Math.max(0.2, parseFloat((z / 1.25).toFixed(3))));
  const zoomReset = () => setZoom(1);

  // Fits the view to the union of both foot regions instead of the whole scan page — the page
  // is usually much bigger than the feet, so a plain fit-to-page leaves them tiny.
  // Reads dimensions straight off the DOM element instead of the natW/natH state: reopening a
  // saved document swaps page.base64 (raw fetch lands, then a re-encode — see the [page?.base64]
  // effect above) which resets natW/natH to 0 for a beat even though the img has already painted.
  // A click landing in that window used to silently no-op (or worse, compute against a stale
  // fitScale of 0/1) — reading the live element sidesteps the race entirely.
  const zoomToFeet = () => {
    const regions = effectiveAnnotations.filter(a => a.type === "region");
    const liveNatW = imgRef.current?.naturalWidth || 0;
    const liveNatH = imgRef.current?.naturalHeight || 0;
    if (!regions.length || !liveNatW || !liveNatH) return;
    const minX = Math.min(...regions.map(r => Math.min(r.x1, r.x2)));
    const maxX = Math.max(...regions.map(r => Math.max(r.x1, r.x2)));
    const minY = Math.min(...regions.map(r => Math.min(r.y1, r.y2)));
    const maxY = Math.max(...regions.map(r => Math.max(r.y1, r.y2)));
    const bboxW = maxX - minX, bboxH = maxY - minY;
    if (bboxW <= 0 || bboxH <= 0) return;

    const liveFitScale = Math.min(imgMax.w / liveNatW, imgMax.h / liveNatH);
    const target = Math.min(5, Math.max(1,
      Math.min(imgMax.w / (bboxW * liveFitScale), imgMax.h / (bboxH * liveFitScale)) * 0.92
    ));
    zoomFeetTargetRef.current = { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
    // natW/natH may still be lagging React state — nudge them to the live values too so fitScale/
    // displayW (which do read state) don't stay stuck on stale 0 for this same render cycle.
    if (natW !== liveNatW) setNatW(liveNatW);
    if (natH !== liveNatH) setNatH(liveNatH);
    setZoom(parseFloat(target.toFixed(3)));
  };

  // ── Redraw ────────────────────────────────────────────────────────
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.naturalWidth) return;

    const scale = getScale();
    canvas.width  = img.naturalWidth * scale;
    canvas.height = img.naturalHeight * scale;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const draggingPadFoot = resizing?.corner === "padOffset" ? effectiveAnnotations[resizing.annIdx]?.foot : null;
    drawAnnotations(ctx, {
      annotations: effectiveAnnotations,
      padSizes, padSizesPerFoot, dpi, scale, showLabels,
      selectedIndex: selected,
      activeFoot,
      draggingPadFoot,
    });

    if (drawing) {
      if (tool === "region") {
        const lx=Math.min(drawing.x1,drawing.x2), rx=Math.max(drawing.x1,drawing.x2);
        const ty=Math.min(drawing.y1,drawing.y2), by=Math.max(drawing.y1,drawing.y2);
        const color = FOOT_COLORS[activeFoot] || "#2563EB";
        ctx.save();
        ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.setLineDash([8,4]);
        ctx.strokeRect(lx*scale, ty*scale, (rx-lx)*scale, (by-ty)*scale);
        ctx.restore();
      } else {
        const { x1, y1, x2, y2 } = drawing;
        const color = LINE_COLORS[tool] || "#1976d2";
        ctx.save();
        ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.setLineDash([5,3]);
        ctx.beginPath(); ctx.moveTo(x1*scale, y1*scale); ctx.lineTo(x2*scale, y2*scale); ctx.stroke();
        if (showLabels) {
          const mm = pxToMm(dist(x1,y1,x2,y2), dpi);
          const mx=(x1+x2)/2*scale, my=(y1+y2)/2*scale;
          const label = `${mm} мм`;
          ctx.font = "bold 13px sans-serif";
          const tw = ctx.measureText(label).width;
          ctx.fillStyle = `${color}cc`;
          ctx.beginPath(); ctx.roundRect(mx-tw/2-4, my-16, tw+8, 20, 4); ctx.fill();
          ctx.fillStyle="#fff"; ctx.fillText(label, mx-tw/2, my-1);
        }
        ctx.restore();
      }
    }
  }, [effectiveAnnotations, drawing, selected, dpi, tool, getScale, activeFoot, showLabels, padSizes, padSizesPerFoot, resizing]);

  useEffect(() => { redraw(); }, [redraw]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const ro = new ResizeObserver(() => redraw());
    ro.observe(img);
    return () => ro.disconnect();
  }, [redraw, page]);

  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const pad = 32;
    const update = () => setImgMax({ w: Math.max(100, area.clientWidth-pad), h: Math.max(100, area.clientHeight-pad) });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(area);
    return () => ro.disconnect();
  }, [fullscreen]);

  // Runs after `zoom` (set by zoomToFeet) has been applied and the img/canvas have re-rendered
  // at the new size — scrolls so the feet bbox center lands in the middle of the viewport.
  // Uses live getBoundingClientRect deltas (not hand-computed margins) so it stays correct
  // regardless of .viewer-canvas-wrap's CSS. No-ops for zoomIn/zoomOut/reset/wheel, which leave
  // the ref empty.
  useLayoutEffect(() => {
    const target = zoomFeetTargetRef.current;
    const area = areaRef.current;
    const img = imgRef.current;
    if (!target || !area || !img || !img.naturalWidth) return;
    zoomFeetTargetRef.current = null;
    const areaRect = area.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const scale = imgRect.width / img.naturalWidth;
    const targetX = (imgRect.left - areaRect.left) + area.scrollLeft + target.cx * scale;
    const targetY = (imgRect.top - areaRect.top) + area.scrollTop + target.cy * scale;
    area.scrollLeft = targetX - area.clientWidth / 2;
    area.scrollTop = targetY - area.clientHeight / 2;
  }, [zoom]);

  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setZoom(z => Math.min(5, Math.max(0.2, parseFloat((z * factor).toFixed(3)))));
    };
    area.addEventListener("wheel", onWheel, { passive: false });
    return () => area.removeEventListener("wheel", onWheel);
  }, [fullscreen]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [fullscreen]);

  useEffect(() => { setZoom(1); setNatW(0); setNatH(0); }, [page?.base64]);
  useEffect(() => { if (fullscreen) setZoom(1); }, [fullscreen]);

  const handleMouseDown = useCallback((e) => {
    if (!page) return;
    const { x, y } = evToImg(e);
    const scale = getScale();
    const hitR = CORNER_HIT / scale;

    if (tool === "select") {
      // Валик — проверяем первым: он сидит прямо на пересечении ширинной и метатарзальной
      // линий, где иначе конкурируют хит-зоны их концов/тела. Двигать можно только вдоль оси
      // метатарзальной линии (см. computeFootPads) — смещение хранится на самой линии
      // (padOffset), а не как отдельная аннотация.
      const pads = computeFootPads(effectiveAnnotations);
      for (const pad of pads) {
        const padSize = padSizes.find(s => s.id === resolvePadSizeId(padSizes, padSizesPerFoot, pad.foot));
        if (!padSize) continue;
        const padHitR = Math.max((padSize.length_mm / 25.4) * dpi, (padSize.width_mm / 25.4) * dpi) / 2;
        if (dist(x, y, pad.pt.x, pad.pt.y) < padHitR) {
          setSelected(pad.metIdx);
          setResizing({
            annIdx: pad.metIdx, corner: "padOffset", axis: pad.axis,
            startOffset: effectiveAnnotations[pad.metIdx]?.padOffset || 0, startX: x, startY: y,
          });
          return;
        }
      }
      // Check region corners
      for (let i = effectiveAnnotations.length - 1; i >= 0; i--) {
        const ann = effectiveAnnotations[i];
        if (ann.type !== "region") continue;
        const corners = regionCorners(ann);
        for (const [key, [cx, cy]] of Object.entries(corners)) {
          if (dist(x, y, cx, cy) < hitR) {
            setSelected(i); setResizing({ annIdx: i, corner: key }); return;
          }
        }
      }
      // Check line endpoints
      for (let i = effectiveAnnotations.length - 1; i >= 0; i--) {
        const ann = effectiveAnnotations[i];
        if (ann.type === "region") continue;
        if (dist(x, y, ann.x1, ann.y1) < hitR * 2) {
          setSelected(i); setResizing({ annIdx: i, corner: "p1" }); return;
        }
        if (dist(x, y, ann.x2, ann.y2) < hitR * 2) {
          setSelected(i); setResizing({ annIdx: i, corner: "p2" }); return;
        }
      }
      let found = -1;
      effectiveAnnotations.forEach((ann, i) => {
        if (ann.type === "region") {
          const lx=Math.min(ann.x1,ann.x2), rx=Math.max(ann.x1,ann.x2);
          const ty=Math.min(ann.y1,ann.y2), by=Math.max(ann.y1,ann.y2);
          if (x>=lx-hitR && x<=rx+hitR && y>=ty-hitR && y<=by+hitR) found = i;
        } else {
          const { x1, y1, x2, y2 } = ann;
          if (dist(x,y,x1,y1)<hitR*2 || dist(x,y,x2,y2)<hitR*2 || dist(x,y,(x1+x2)/2,(y1+y2)/2)<hitR*3) found = i;
        }
      });
      setSelected(found >= 0 ? found : null);
      return;
    }

    setDrawing({ x1: x, y1: y, x2: x, y2: y });
    setSelected(null);
  }, [tool, page, evToImg, annotations, effectiveAnnotations, getScale, padSizes, padSizesPerFoot, dpi]);

  const handleMouseMove = useCallback((e) => {
    if (!page) return;
    const { x, y } = evToImg(e);
    if (resizing) {
      const base = localResizeAnns ?? annotations;
      const ann = base[resizing.annIdx];
      const updated = { ...ann };
      if (resizing.corner === "tl") { updated.x1 = x; updated.y1 = y; }
      else if (resizing.corner === "tr") { updated.x2 = x; updated.y1 = y; }
      else if (resizing.corner === "bl") { updated.x1 = x; updated.y2 = y; }
      else if (resizing.corner === "br") { updated.x2 = x; updated.y2 = y; }
      // Edge handles move only one side, keeping the opposite edge (and so only width OR
      // only height). x1/x2 aren't guaranteed ordered (a prior corner drag may have flipped
      // the rect), so pick whichever field currently holds the min/max rather than assuming x1.
      else if (resizing.corner === "l") { if (ann.x1 <= ann.x2) updated.x1 = x; else updated.x2 = x; }
      else if (resizing.corner === "r") { if (ann.x1 <= ann.x2) updated.x2 = x; else updated.x1 = x; }
      else if (resizing.corner === "t") { if (ann.y1 <= ann.y2) updated.y1 = y; else updated.y2 = y; }
      else if (resizing.corner === "b") { if (ann.y1 <= ann.y2) updated.y2 = y; else updated.y1 = y; }
      else if (resizing.corner === "p1") { updated.x1 = x; updated.y1 = y; }
      else if (resizing.corner === "p2") { updated.x2 = x; updated.y2 = y; }
      else if (resizing.corner === "padOffset") {
        // Проецируем движение мыши на ось линии — перпендикулярная составляющая игнорируется,
        // так что валик физически не может сойти с метатарзальной линии.
        const { ux, uy } = resizing.axis;
        const proj = (x - resizing.startX) * ux + (y - resizing.startY) * uy;
        updated.padOffset = resizing.startOffset + proj;
      }
      setLocalResizeAnns(base.map((a, i) => i === resizing.annIdx ? updated : a));
      return;
    }
    if (drawing) setDrawing(prev => ({ ...prev, x2: x, y2: y }));
  }, [drawing, resizing, evToImg, annotations, localResizeAnns, page]);

  const handleMouseUp = useCallback(() => {
    if (resizing) {
      if (localResizeAnns) {
        let finalAnns = localResizeAnns;
        // Recalculate distanceMm when a line endpoint was moved
        if (resizing.corner === "p1" || resizing.corner === "p2") {
          const ann = localResizeAnns[resizing.annIdx];
          if (ann && ann.type !== "region") {
            const d = dist(ann.x1, ann.y1, ann.x2, ann.y2);
            const updated = { ...ann, distanceMm: pxToMm(d, dpi) };
            finalAnns = localResizeAnns.map((a, i) => i === resizing.annIdx ? updated : a);
          }
        }
        onAnnotationsChange(finalAnns);
        setLocalResizeAnns(null);
      }
      setResizing(null);
      return;
    }
    if (!drawing) return;
    const { x1, y1, x2, y2 } = drawing;
    if (tool === "region") {
      const w = Math.abs(x2-x1), h = Math.abs(y2-y1);
      if (w > 10 && h > 10) {
        // Only one region per foot — drawing a new one replaces the old instead of stacking.
        const base = annotations.filter(a => !(a.type === "region" && a.foot === activeFoot));
        onAnnotationsChange([...base, { type: "region", x1, y1, x2, y2, foot: activeFoot }]);
      }
    } else {
      const d = dist(x1, y1, x2, y2);
      if (d > 3) {
        // Same one-per-foot rule for the structural measurements (Разм./Шир./Пят.) — the free
        // "Линейка" (measure) tool is exempt and can be added any number of times.
        const base = tool === "measure"
          ? annotations
          : annotations.filter(a => !(a.type === tool && a.foot === activeFoot));
        onAnnotationsChange([...base, { type: tool, x1, y1, x2, y2, distanceMm: pxToMm(d, dpi), foot: activeFoot }]);
      }
    }
    setDrawing(null);
  }, [drawing, resizing, localResizeAnns, dpi, annotations, onAnnotationsChange, tool, activeFoot]);

  const deleteSelected = () => {
    if (selected === null) return;
    onAnnotationsChange(effectiveAnnotations.filter((_, i) => i !== selected));
    setLocalResizeAnns(null);
    setSelected(null);
  };

  // ── Empty state ───────────────────────────────────────────────────
  if (!page) {
    return (
      <div className="image-viewer">
        <div className="viewer-empty">
          <span className="viewer-empty-icon">🖨</span>
          <span className="viewer-empty-text">Подключите сканер и нажмите «Сканировать»</span>
        </div>
      </div>
    );
  }

  // ── Config ────────────────────────────────────────────────────────
  const GENERAL_TOOLS = [
    { id: "select",  label: "Выбор",   icon: <MousePointer2 className="h-3.5 w-3.5" />, title: "Выбрать/изменить" },
    { id: "measure", label: "Линейка", icon: <Ruler className="h-3.5 w-3.5" />,         title: "Произвольное расстояние" },
    { id: "region",  label: "Область", icon: <Square className="h-3.5 w-3.5" />,        title: "Выделить область" },
  ];
  const MEASURE_TOOLS = [
    { id: "size_line",       label: "Разм.", icon: <ArrowUpDown className="h-3.5 w-3.5" />,    title: "Размерная (большой палец → пятка)" },
    { id: "width_line",      label: "Шир.",  icon: <ArrowLeftRight className="h-3.5 w-3.5" />, title: "Ширина стопы (поперёк)" },
    { id: "metatarsal_line", label: "Мет.",  icon: <CornerDownRight className="h-3.5 w-3.5" />,title: "Метатарзальная (центр ширины → пятка)" },
    { id: "heel_line",       label: "Пят.",  icon: <Minus className="h-3.5 w-3.5" />,          title: "Пяточная (ширина пятки)" },
  ];
  const VIEWS = [
    { id: "wb",       label: "Белый фон" },
    { id: "heat",     label: "Тепловая" },
  ];

  const areaStyle = zoom > 1
    ? { overflow: "auto", alignItems: "flex-start", justifyContent: "flex-start" }
    : { overflow: "hidden" };

  const hasMetPad = effectiveAnnotations.some(a => a.type === "width_line")
    && effectiveAnnotations.some(a => a.type === "metatarsal_line")
    && padSizes?.length > 0;

  const generalToolValue = GENERAL_TOOLS.some(t => t.id === tool) ? tool : "";

  // ── Toolbar ───────────────────────────────────────────────────────
  const toolbar = (
    <div className="flex items-center gap-1.5 px-2 bg-white border-b border-slate-200 h-10 flex-shrink-0">

      {/* General tool dropdown */}
      <ToolDropdown tools={GENERAL_TOOLS} value={generalToolValue} onChange={setTool} />

      <div className="w-px h-5 bg-slate-200 flex-shrink-0" />

      {/* Medical measurement tools */}
      <div className="flex items-center gap-0.5 bg-blue-50 rounded-lg p-0.5 flex-shrink-0">
        {MEASURE_TOOLS.map(t => (
          <button key={t.id} title={t.title} onClick={() => setTool(t.id)}
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all",
              tool === t.id ? "bg-white shadow-sm" : "text-slate-400 hover:text-slate-700"
            )}
            style={tool === t.id ? { color: LINE_COLORS[t.id] } : {}}>
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Pad size dropdown (conditional) */}
      {hasMetPad && (
        <>
          <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
          <PadSizeDropdown sizes={padSizes} value={currentPadSizeId} onChange={handlePadSizeChange} />
        </>
      )}

      {/* Labels toggle */}
      <button title={showLabels ? "Скрыть все подписи" : "Показать подписи"}
        onClick={() => setShowLabels(v => !v)}
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all flex-shrink-0",
          showLabels ? "bg-blue-50 text-blue-600" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
        )}>
        <Tag className="h-3.5 w-3.5" />
        <span>Подписи</span>
      </button>

      {/* Delete selected */}
      {selected !== null && (
        <button onClick={deleteSelected}
          className="flex items-center gap-1 px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors flex-shrink-0">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Right: clear + pagination */}
      <div className="ml-auto flex items-center gap-1 flex-shrink-0">
        {annotations.length > 0 && (
          <button onClick={() => { onAnnotationsChange([]); setSelected(null); }}
            className="text-xs text-slate-400 hover:text-slate-700 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors">
            Очистить
          </button>
        )}
        {pages.length > 1 && (
          <div className="flex items-center gap-1">
            <button className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors"
              disabled={currentPage === 0} onClick={() => onPageChange(currentPage - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-slate-500 font-medium">{currentPage + 1}/{pages.length}</span>
            <button className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors"
              disabled={currentPage >= pages.length - 1} onClick={() => onPageChange(currentPage + 1)}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ── Canvas area — shared between normal and fullscreen ────────────
  // Two layers on purpose: viewer-canvas-outer never scrolls and is the positioning context for
  // every absolute overlay control (view switcher, foot size, rotate/flip/undo, zoom controls) —
  // they need to stay pinned to the visible frame. viewer-canvas-area (ref=areaRef) is the actual
  // scrollable element once zoomed; it used to also host the overlay controls directly, which made
  // them scroll away with the image the moment zoomToFeet (or manual zoom) caused any scrolling.
  const canvasArea = (
    <div className="viewer-canvas-outer">

      {/* TOP-LEFT: View mode switcher */}
      {onScanViewChange && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-0.5 bg-black/30 backdrop-blur-sm rounded-xl p-1">
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => onScanViewChange(v.id)}
              className={cn(
                "px-2.5 py-1 rounded-lg text-xs font-semibold transition-all",
                scanView === v.id ? "bg-white/90 text-slate-900" : "text-white/65 hover:text-white/90"
              )}>
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* TOP-CENTER: foot size (left) + foot selector + foot size (right) */}
      {onActiveFootChange && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          {onScanParamsChange && (
            <FootSizeWidget side="left" align="left" scanParams={scanParams} onScanParamsChange={onScanParamsChange} allAnnotations={allAnnotations} />
          )}
          <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-xl p-1">
            {[
              { side: "left",  label: "Левая",  color: "bg-blue-500" },
              { side: "right", label: "Правая", color: "bg-green-500" },
            ].map(f => (
              <button key={f.side} onClick={() => onActiveFootChange(f.side)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all",
                  activeFoot === f.side ? `${f.color} text-white shadow-sm` : "text-white/60 hover:text-white/90"
                )}>
                {f.label}
              </button>
            ))}
          </div>
          {onScanParamsChange && (
            <FootSizeWidget side="right" align="right" scanParams={scanParams} onScanParamsChange={onScanParamsChange} allAnnotations={allAnnotations} />
          )}
        </div>
      )}

      {/* TOP-RIGHT: Auto-detect + Rotate + Flip + Undo */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
        {onAutoDetectFeet && (
          <button
            title="Определить стопы заново"
            onClick={onAutoDetectFeet}
            className="p-2 rounded-xl backdrop-blur-sm bg-black/30 text-white hover:bg-black/50 transition-all"
          >
            <ScanSearch className="h-4 w-4" />
          </button>
        )}
        {onRotatePage && (
          <button
            title="Повернуть против часовой"
            onClick={() => onRotatePage(-1)}
            className="p-2 rounded-xl backdrop-blur-sm bg-black/30 text-white hover:bg-black/50 transition-all"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
        {onRotatePage && (
          <button
            title="Повернуть по часовой"
            onClick={() => onRotatePage(1)}
            className="p-2 rounded-xl backdrop-blur-sm bg-black/30 text-white hover:bg-black/50 transition-all"
          >
            <RotateCw className="h-4 w-4" />
          </button>
        )}
        {onFlipPage && (
          <button
            title="Отразить по горизонтали"
            onClick={onFlipPage}
            className="p-2 rounded-xl backdrop-blur-sm bg-black/30 text-white hover:bg-black/50 transition-all"
          >
            <FlipHorizontal className="h-4 w-4" />
          </button>
        )}
        <button
          title="Отменить (Ctrl+Z)"
          onClick={canUndo ? onUndo : undefined}
          className={cn(
            "p-2 rounded-xl backdrop-blur-sm transition-all",
            canUndo
              ? "bg-black/30 text-white hover:bg-black/50"
              : "bg-black/15 text-white/25 cursor-default"
          )}
        >
          <Undo2 className="h-4 w-4" />
        </button>
      </div>

      {/* BOTTOM-LEFT: Zoom + Fullscreen */}
      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-0.5 bg-black/30 backdrop-blur-sm rounded-xl p-1">
        <button title="Уменьшить (колесо мыши)" onClick={zoomOut}
          className="p-1.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all">
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <button title="Сбросить масштаб" onClick={zoomReset}
          className="w-11 text-center text-[11px] font-bold text-white hover:bg-white/10 rounded-lg py-1 transition-all">
          {Math.round(zoom * 100)}%
        </button>
        <button title="Увеличить (колесо мыши)" onClick={zoomIn}
          className="p-1.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all">
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <button title="Уместить стопы" onClick={zoomToFeet}
          className="p-1.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all">
          <Focus className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-4 bg-white/25 mx-0.5" />
        <button
          title={fullscreen ? "Выйти (Esc)" : "На весь экран"}
          onClick={() => setFullscreen(f => !f)}
          className="p-1.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all">
          {fullscreen ? <X className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className="viewer-canvas-area" ref={areaRef} style={areaStyle}>
        <div className="viewer-canvas-wrap" style={zoom > 1 ? { margin: "16px" } : {}}>
          <img
            ref={imgRef}
            className="viewer-img"
            src={page.base64}
            alt={`Страница ${currentPage + 1}`}
            style={displayW ? { width: displayW, height: displayH } : { maxWidth: imgMax.w, maxHeight: imgMax.h }}
            onLoad={() => {
              const img = imgRef.current;
              if (img) { setNatW(img.naturalWidth); setNatH(img.naturalHeight); }
              redraw();
            }}
            draggable={false}
          />
          <canvas
            ref={canvasRef}
            className={`viewer-canvas ${tool === "select" ? "select" : tool === "region" ? "crosshair" : "measuring"}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => { if (drawing) setDrawing(null); if (resizing) setResizing(null); }}
          />
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────
  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[9999] flex flex-col bg-white">
        {toolbar}
        {canvasArea}
      </div>
    );
  }

  return (
    <div className="image-viewer">
      {toolbar}
      {canvasArea}
    </div>
  );
}
